import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { authApi } from '../api/auth'
import { useAuth } from '../contexts/AuthContext'
import { useThemeContext } from '../contexts/ThemeContext'
import { UserContextResponse, ContextSelectionRequest, UserContext } from '../types/auth'
import { NivelAcesso } from '../types/auth'

const ContextSelectionPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { updateContext } = useAuth()
  const { setThemeId, mode, toggleMode } = useThemeContext()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [context, setContext] = useState<UserContextResponse | null>(null)
  const [selectedRole, setSelectedRole] = useState<'SUPER_ADMIN' | 'ADMINISTRADOR' | 'USUARIO' | ''>('')
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (role?: 'SUPER_ADMIN' | 'ADMINISTRADOR' | 'USUARIO', empresaId?: number) => {
    const finalRole = role || selectedRole
    const finalEmpresaId = empresaId || selectedEmpresaId

    if (!finalRole) {
      setError(t('context.selectMode'))
      return
    }

    // Validar se precisa de empresa
    // Precisa de empresa se:
    // - Role é USUARIO (sempre)
    // - Role é ADMINISTRADOR (sempre, tanto para Super Admin quanto Admin normal)
    // Precisa de empresa se:
    // - Role é USUARIO (sempre)
    // - Para ADMINISTRADOR e SUPER_ADMIN, a empresa é opcional no painel ADM
    const needsEmpresa = finalRole === 'USUARIO'

    if (needsEmpresa && !finalEmpresaId) {
      setError(t('context.selectCompanyError'))
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      // Calcular nível de acesso efetivo
      let nivel_acesso_efetivo = context ? context.nivel_acesso : NivelAcesso.USUARIO_PADRAO
      if (finalRole === 'USUARIO') {
        nivel_acesso_efetivo = NivelAcesso.USUARIO_PADRAO
      } else if (finalRole === 'ADMINISTRADOR') {
        nivel_acesso_efetivo = NivelAcesso.ADMINISTRADOR
      } else if (finalRole === 'SUPER_ADMIN') {
        nivel_acesso_efetivo = NivelAcesso.SUPER_ADMIN
      }

      // Determinar empresa_id baseado no role
      let empresaIdForContext: number | null = null
      if (finalRole === 'USUARIO') {
        empresaIdForContext = finalEmpresaId || null
      } else if (finalRole === 'ADMINISTRADOR') {
        // Admin não precisa obrigatoriamente de empresa no painel ADM
        empresaIdForContext = finalEmpresaId || null
      } else if (finalRole === 'SUPER_ADMIN') {
        // Super Admin não precisa de empresa
        empresaIdForContext = null
      }

      const contextSelection: ContextSelectionRequest = {
        role: finalRole,
        empresa_id: empresaIdForContext,
      }

      await authApi.selectContext(contextSelection)

      // Armazenar contexto completo
      const selectedEmpresa = context?.empresas.find(e => e.id_empresa === empresaIdForContext)
      const userContext: UserContext = {
        role: finalRole,
        empresa_id: empresaIdForContext,
        nivel_acesso_efetivo,
        preferencias: selectedEmpresa?.preferencias || {},
        empresa_nome: selectedEmpresa?.nome_empresa || null,
        empresa_razao_social: selectedEmpresa?.razao_social || null,
        empresa_cnpj: selectedEmpresa?.cnpj || null
      }
      
      // Atualizar contexto via AuthContext (atualiza localStorage e estado React)
      updateContext(userContext)
      
      try {
        const ui = (selectedEmpresa?.preferencias as any)?.ui
        if (ui?.themeId) setThemeId(ui.themeId)
        if (ui?.mode && (ui.mode === 'light' || ui.mode === 'dark') && ui.mode !== mode) {
          toggleMode()
        }
      } catch {}

      // Redirecionar para home
      navigate('/', { replace: true })
    } catch (err: any) {
      setError(err.response?.data?.detail || t('context.saveError'))
      setSubmitting(false)
    }
  }

  useEffect(() => {
    const loadContext = async () => {
      try {
        setLoading(true)
        const contextData = await authApi.getUserContext()
        setContext(contextData)
        
        // Se houver apenas 1 empresa cadastrada, entrar diretamente como USUARIO nessa empresa
        if (contextData.empresas && contextData.empresas.length === 1) {
          const unicaEmpresa = contextData.empresas[0]
          const roleToSelect = 'USUARIO'
          const nivel_acesso_efetivo = NivelAcesso.USUARIO_PADRAO
          
          const contextSelection: ContextSelectionRequest = {
            role: roleToSelect,
            empresa_id: unicaEmpresa.id_empresa,
          }

          await authApi.selectContext(contextSelection)

          const userContext: UserContext = {
            role: roleToSelect,
            empresa_id: unicaEmpresa.id_empresa,
            nivel_acesso_efetivo,
            preferencias: unicaEmpresa.preferencias || {},
            empresa_nome: unicaEmpresa.nome_empresa || null,
            empresa_razao_social: unicaEmpresa.razao_social || null,
            empresa_cnpj: unicaEmpresa.cnpj || null
          }
          
          updateContext(userContext)
          
          try {
            const ui = (unicaEmpresa.preferencias as any)?.ui
            if (ui?.themeId) setThemeId(ui.themeId)
            if (ui?.mode && (ui.mode === 'light' || ui.mode === 'dark') && ui.mode !== mode) {
              toggleMode()
            }
          } catch {}

          navigate('/', { replace: true })
        } else if (contextData.empresas && contextData.empresas.length === 0 && contextData.can_act_as_super_admin) {
          // Super Admin sem empresa cadastrada
          const roleToSelect = 'SUPER_ADMIN'
          const contextSelection: ContextSelectionRequest = {
            role: roleToSelect,
            empresa_id: null,
          }

          await authApi.selectContext(contextSelection)

          const userContext: UserContext = {
            role: roleToSelect,
            empresa_id: null,
            nivel_acesso_efetivo: NivelAcesso.SUPER_ADMIN,
            preferencias: {},
            empresa_nome: null,
            empresa_razao_social: null,
            empresa_cnpj: null
          }
          
          updateContext(userContext)
          navigate('/', { replace: true })
        } else {
          setLoading(false)
        }
      } catch (err: any) {
        setError(err.response?.data?.detail || t('context.loadError'))
        setLoading(false)
      }
    }

    loadContext()
  }, [t, navigate, updateContext, setThemeId, toggleMode, mode])

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error && !context) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    )
  }

  if (!context) {
    return null
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', py: 4 }}>
        <Paper sx={{ p: 4, width: '100%' }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            {t('context.selectTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
            {t('context.selectSubtitle')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
            <FormLabel component="legend">{t('context.modeOfAction')}</FormLabel>
            <RadioGroup
              value={selectedRole}
              onChange={(e) => {
                setSelectedRole(e.target.value as 'SUPER_ADMIN' | 'ADMINISTRADOR' | 'USUARIO')
                // Limpar empresa apenas se escolher SUPER_ADMIN
                if (e.target.value === 'SUPER_ADMIN') {
                  setSelectedEmpresaId(null)
                }
              }}
            >
              {context.can_act_as_super_admin && (
                <FormControlLabel
                  value="SUPER_ADMIN"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {t('context.superAdmin')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('context.superAdminDescription')}
                      </Typography>
                    </Box>
                  }
                />
              )}
              {context.can_act_as_admin && (
                <FormControlLabel
                  value="ADMINISTRADOR"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {t('context.administrator')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('context.administratorDescription')}
                      </Typography>
                    </Box>
                  }
                />
              )}
              {(context.can_act_as_user || context.nivel_acesso === NivelAcesso.SUPER_ADMIN) && (
                <FormControlLabel
                  value="USUARIO"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {t('context.user')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('context.userDescription')}
                      </Typography>
                    </Box>
                  }
                />
              )}
            </RadioGroup>
          </FormControl>

          {/* Mostrar seleção de empresa para USUARIO ou ADMINISTRADOR (sempre) */}
          {(selectedRole === 'USUARIO' || selectedRole === 'ADMINISTRADOR') && (
            <>
              {context.empresas && context.empresas.length > 0 ? (
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="empresa-select-label">{t('context.companyRequired')}</InputLabel>
                  <Select
                    labelId="empresa-select-label"
                    value={selectedEmpresaId || ''}
                    label={t('context.companyRequired')}
                    onChange={(e) => setSelectedEmpresaId(Number(e.target.value))}
                    required
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      <em>{t('context.selectCompany')}</em>
                    </MenuItem>
                    {context.empresas.map((empresa) => (
                      <MenuItem key={empresa.id_empresa} value={empresa.id_empresa}>
                        {empresa.nome_empresa}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  {context.empresas && context.empresas.length === 0
                    ? `${t('context.noCompanyAvailable')} (Total: ${context.empresas?.length || 0})`
                    : t('common.loading')}
                </Alert>
              )}
            </>
          )}

          {selectedRole && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
              <Button
                variant="contained"
                onClick={() => handleSubmit()}
                disabled={submitting}
                size="large"
              >
                {submitting ? <CircularProgress size={24} /> : t('context.continue')}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  )
}

export default ContextSelectionPage
